// Certificate issuance + lookup.
import { supabase } from '../integrations/supabase/client';
import type { Certificate } from '../types/assess';

export async function maybeIssueCertificate(userId: string, courseId: string): Promise<{ certificate: Certificate; created: boolean } | null> {
  // Idempotent: returns existing or inserts on 100% completion (checked by caller).
  const { data: existing } = await supabase.from('certificates').select('*').eq('user_id', userId).eq('course_id', courseId).maybeSingle();
  if (existing) return { certificate: existing as unknown as Certificate, created: false };
  const { data, error } = await supabase.from('certificates').insert({ user_id: userId, course_id: courseId }).select().single();
  if (error) return null;
  return { certificate: data as unknown as Certificate, created: true };
}

export async function getMyCertificates(userId: string): Promise<Certificate[]> {
  const { data } = await supabase.from('certificates').select('*, courses(title)').eq('user_id', userId).order('issued_at', { ascending: false });
  return (((data ?? []) as unknown as (Certificate & { courses?: { title?: string } })[]).map((c) => ({
    ...c,
    course_title: c.courses?.title,
  })));
}

export interface CertificateVerification {
  code: string;
  course_id: string;
  course_title: string;
  issued_at: string;
  revoked: boolean;
  revoked_at: string | null;
  revoked_reason: string | null;
}

export async function verifyCertificate(code: string): Promise<CertificateVerification | null> {
  const { data, error } = await supabase.from('certificates_public').select('*').ilike('code', code.trim()).maybeSingle();
  if (!error) return (data as unknown as CertificateVerification) ?? null;
  // Pre-migration fallback (public table read policy may still be present).
  const { data: legacy } = await supabase.from('certificates').select('*').ilike('code', code.trim()).maybeSingle();
  if (!legacy) return null;
  const row = legacy as unknown as Certificate;
  return {
    code: row.code,
    course_id: row.course_id,
    course_title: row.course_title || row.course_id,
    issued_at: row.issued_at,
    revoked: !!row.revoked,
    revoked_at: row.revoked_at ?? null,
    revoked_reason: row.revoked_reason ?? null,
  };
}
